import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HERO_IMAGE } from "@/lib/constants";

export default function LoginPage() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("demo@thecloseout.com");
  const [password, setPassword] = useState("Demo2026!");
  const [busy, setBusy] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(null);  // {user_id}
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.needs_2fa) {
        setNeeds2fa({ user_id: data.user_id });
        return;
      }
      localStorage.setItem("co_token", data.token);
      localStorage.setItem("co_user", JSON.stringify(data.user));
      window.location.href = "/app/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-2fa", { user_id: needs2fa.user_id, code });
      localStorage.setItem("co_token", data.token);
      localStorage.setItem("co_user", JSON.stringify(data.user));
      window.location.href = "/app/dashboard";
    } catch { toast.error("Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#14100c] text-[#f4ede2]">
      <div className="relative hidden md:block">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 side-scrim" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
            <div className="w-9 h-9 rounded bg-gradient-to-br from-[#d4a574] to-[#a67b4c] flex items-center justify-center text-[#14100c] font-black font-display">C</div>
            <div>
              <div className="font-display font-bold text-[16px]">The Close Out</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#d4a574]">Distress Intelligence</div>
            </div>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight mb-3">Find opportunities before everyone else.</h2>
            <p className="text-[#b8ac96] text-sm max-w-md">240+ live distressed opportunities across the US. AI-scored. Continuously indexed.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        {!needs2fa ? (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Sign In</div>
              <h1 className="font-display text-3xl font-bold">Welcome back.</h1>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="bg-[#1c1712] border-[#33291f]" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Password</Label><Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="bg-[#1c1712] border-[#33291f]" /></div>
            <button type="submit" disabled={busy} data-testid="login-submit" className="w-full py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988] disabled:opacity-60">{busy ? "Signing in…" : "Sign In →"}</button>
            <div className="text-center text-xs text-[#7a6d5a]">No account? <Link to="/register" className="text-[#d4a574] hover:text-[#e8b988]">Request access</Link></div>
            <div className="glass rounded p-3 text-[11px] text-[#b8ac96] space-y-1">
              <div className="text-[#d4a574] uppercase tracking-wider text-[10px] font-semibold">Demo Credentials</div>
              <div className="font-mono">demo@thecloseout.com · Demo2026!</div>
            </div>
          </form>
        ) : (
          <form onSubmit={submit2fa} className="w-full max-w-sm space-y-5" data-testid="login-2fa-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Two-Factor</div>
              <h1 className="font-display text-3xl font-bold">Enter your code.</h1>
              <p className="text-sm text-[#7a6d5a] mt-1">Open your authenticator app.</p>
            </div>
            <Input required maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="login-2fa-code" className="bg-[#1c1712] border-[#33291f] font-mono text-center tracking-widest text-lg h-12" />
            <button type="submit" disabled={busy || code.length < 6} data-testid="login-2fa-submit" className="w-full py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988] disabled:opacity-60">{busy ? "Verifying…" : "Verify →"}</button>
            <button type="button" onClick={() => setNeeds2fa(null)} className="w-full text-[12px] text-[#7a6d5a] hover:text-[#d4a574]">← Back</button>
          </form>
        )}
      </div>
    </div>
  );
}
